import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Phone,
  Mail,
  Calendar,
  Download,
  MessageSquare,
  ArrowRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { formatCurrency } from '../../utils';

const steps = [
  {
    number: 1,
    title: 'Review Proposal',
    description: 'Take time to review all equipment, options, and pricing details.',
    icon: CheckCircle,
  },
  {
    number: 2,
    title: 'Ask Questions',
    description: 'Use the feedback feature or contact your rep with any questions.',
    icon: MessageSquare,
  },
  {
    number: 3,
    title: 'Confirm Details',
    description: 'Verify delivery location, timeline, and payment terms.',
    icon: Calendar,
  },
  {
    number: 4,
    title: 'Accept Proposal',
    description: 'Click accept below to proceed with your order.',
    icon: Sparkles,
  },
];

interface NextStepsSectionProps {
  acceptedTotal?: number;  // For service proposals - the sum of accepted tasks
}

export function NextStepsSection({ acceptedTotal }: NextStepsSectionProps) {
  const proposal = useProposalData();
  const { pricing } = proposal;
  const [isAccepting, setIsAccepting] = useState(false);

  // Calculate total ourselves - don't trust pricing.total from JSON
  const calculatedSubtotal = pricing.equipmentSubtotal + pricing.addonsSubtotal + pricing.servicesSubtotal;
  const calculatedSavings = pricing.tradeInCredit + pricing.discounts;
  const calculatedTotal = calculatedSubtotal - calculatedSavings;
  const [isAccepted, setIsAccepted] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // Lazy load PDF generator only when needed
      const { generateProposalPDF } = await import('../../utils/pdfGenerator');
      await generateProposalPDF({
        proposalId: proposal.proposal.id,
        customerCompany: proposal.customer.company,
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleAccept = async () => {
    setIsAccepting(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsAccepting(false);
    setIsAccepted(true);
  };

  return (
    <Section id="next-steps" className="bg-white">
      <SectionHeader
        title="NEXT STEPS"
        subtitle="Ready to Proceed?"
      />

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Steps */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="space-y-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  className="flex gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Step Number */}
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-hul-navy text-white rounded-xl flex items-center justify-center font-display text-xl">
                      {step.number}
                    </div>
                    {index < steps.length - 1 && (
                      <div className="w-0.5 h-full min-h-[40px] bg-gray-200 my-2" />
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 pb-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-lg uppercase tracking-wider text-hul-navy">
                        {step.title}
                      </h3>
                      <Icon className="w-5 h-5 text-hul-red" />
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Accept Card */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {isAccepted ? (
            // Success State
            <motion.div
              className="bg-green-50 rounded-2xl p-8 text-center border-2 border-green-200"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <motion.div
                className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
              >
                <CheckCircle className="w-10 h-10 text-white" />
              </motion.div>

              <h3 className="font-display text-3xl text-green-700 tracking-wide mb-4">
                PROPOSAL ACCEPTED!
              </h3>

              <p className="text-green-700 mb-6">
                Thank you for your business! Your equipment specialist will contact
                you shortly to confirm delivery details and finalize your order.
              </p>

              <div className="p-4 bg-white rounded-xl border border-green-200">
                <div className="text-sm text-gray-500 mb-1">Confirmation Reference</div>
                <div className="font-mono text-lg text-hul-navy">
                  {proposal.proposal.id}-ACC
                </div>
              </div>
            </motion.div>
          ) : (
            // Accept Card
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-hul-red to-hul-red-dark p-6 text-white">
                <h3 className="font-display text-2xl tracking-wide mb-2">
                  READY TO PROCEED?
                </h3>
                <p className="text-white/80">
                  Accept this proposal to get started
                </p>
              </div>

              {/* Card Body */}
              <div className="p-6">
                {/* Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Total Investment</span>
                    <span className="font-mono text-2xl font-bold text-hul-navy">
                      {formatCurrency(acceptedTotal !== undefined ? acceptedTotal : calculatedTotal)}
                    </span>
                  </div>
                  {/* Monthly payment - Hidden for Cash deals */}
                  {proposal.financing?.type !== 'Cash' && proposal.financing?.monthlyPayment && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Or from</span>
                      <span className="font-mono text-hul-red">
                        {formatCurrency(proposal.financing.monthlyPayment)}/mo
                      </span>
                    </div>
                  )}
                </div>

                {/* Accept Button */}
                {proposal.acceptanceEnabled ? (
                  <motion.button
                    onClick={handleAccept}
                    disabled={isAccepting}
                    className="w-full btn-industrial text-xl py-5 disabled:opacity-70"
                    whileHover={{ scale: isAccepting ? 1 : 1.02 }}
                    whileTap={{ scale: isAccepting ? 1 : 0.98 }}
                  >
                    {isAccepting ? (
                      <span className="flex items-center justify-center gap-3">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        Accept Proposal
                        <ArrowRight className="w-6 h-6" />
                      </span>
                    )}
                  </motion.button>
                ) : (
                  <div className="text-center p-4 bg-gray-100 rounded-xl text-gray-600">
                    Contact your representative to proceed with this proposal.
                  </div>
                )}

                {/* Terms Note */}
                <p className="mt-4 text-xs text-gray-500 text-center">
                  By accepting, you agree to proceed with this proposal. Final
                  terms and conditions will be provided in the purchase agreement.
                </p>
              </div>

              {/* Contact Options */}
              <div className="border-t border-gray-100 p-6 bg-gray-50">
                <div className="text-sm text-gray-500 text-center mb-4">
                  Questions? Contact your representative
                </div>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href={`tel:${proposal.salesperson.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-hul-navy hover:text-hul-navy transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Call
                  </a>
                  <a
                    href={`mailto:${proposal.salesperson.email}?subject=Re: Proposal ${proposal.proposal.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-hul-navy hover:text-hul-navy transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                    data-pdf-hide
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-hul-navy hover:text-hul-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingPDF ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </Section>
  );
}
